import md5 from 'md5';
import { renameKeys } from '.';
import { evolve, invertObj, isEmpty, isNil, mapValues, pick, reject } from './fp';
import {
  keysBackwardCompatibilityMapSearchToState,
  keysMapStateToSearch,
  keysMapDataflow,
  keysMapLayout,
  keysMapDisplay,
  keysMapViewer,
  keysMapMap,
} from './constants';
import { qsParse, qsStringify } from './qs';

const keysMapSearchToState = invertObj(keysMapStateToSearch);
const keysMapDataflowInverted = invertObj(keysMapDataflow);
const keysMapLayoutInverted = invertObj(keysMapLayout);
const keysMapDisplayInverted = invertObj(keysMapDisplay);
const keysMapViewerInverted = invertObj(keysMapViewer);
const keysMapMapInverted = invertObj(keysMapMap);

const escapeComma = value => value.replace(',', '%2C');

const fromStateDataflow = dataflow =>
  renameKeys(
    keysMapDataflow,
    pick(['datasourceId', 'dataflowId', 'agencyId', 'version'], dataflow ?? {}),
  );
const fromStateConstraints = constraints =>
  // like R.pipe(R.values, R.mapObjIndexed(...)): produces an index-keyed object
  // ({ 0: [...], 1: [...] }), which qs stringifies as fs[0]=..&fs[1]=..
  // R.values is nil-safe and the history payloads do reset constraints to null
  // (changeTerm, changeLocale), hence the `?? {}`.
  ({
    ...Object.values(constraints ?? {}).map(constraint =>
      Object.values(constraint).map(escapeComma),
    ),
  });
const fromStateLayout = layout => {
  const cleaned = reject(isEmpty, layout ?? {});
  return isEmpty(cleaned) ? null : renameKeys(keysMapLayout, cleaned);
};
const fromStatePeriod = period => {
  const value = period ?? [];
  return isEmpty(value) ? [] : value;
};

export const fromStateToSearch = state => {
  const evolved = evolve(
    {
      constraints: fromStateConstraints,
      dataflow: fromStateDataflow,
      layout: fromStateLayout,
      period: fromStatePeriod,
      display: display => keysMapDisplay[display],
      viewer: viewer => keysMapViewer[viewer],
      map: renameKeys(keysMapMap),
    },
    state,
  );
  const preparedState = renameKeys(
    keysMapStateToSearch,
    reject(value => isEmpty(value) || isNil(value), evolved),
  );
  return qsStringify(preparedState);
};

const toStateBoolean = value => value === 'true';
const toStateSearchConstraints = constraints =>
  Object.values(constraints)
    .map(value => value.split(','))
    .reduce((memo, [_facetId, _constraintId]) => {
      const facetId = decodeURIComponent(_facetId);
      const constraintId = decodeURIComponent(_constraintId);
      return { ...memo, [md5(`${facetId}${constraintId}`)]: { facetId, constraintId } };
    }, {});
const toStateLayout = layout =>
  renameKeys(
    keysMapLayoutInverted,
    mapValues(value => value.split(','), layout),
  );
const toStateTime = time => mapValues(toStateBoolean, time);
const toStatePeriod = period =>
  period.split(',').map(value => (isEmpty(value) ? undefined : value));
const toStateTerm = term => (Array.isArray(term) ? term.join(',') : term);

export const fromSearchToState = search => {
  const state = qsParse(search);
  const renamed = renameKeys(
    keysBackwardCompatibilityMapSearchToState,
    renameKeys(keysMapSearchToState, state),
  );
  return evolve(
    {
      dataflow: renameKeys(keysMapDataflowInverted),
      display: display => keysMapDisplayInverted[display],
      viewer: viewer => keysMapViewerInverted[viewer],
      map: renameKeys(keysMapMapInverted),
      constraints: toStateSearchConstraints,
      hasAccessibility: toStateBoolean,
      hasDataAvailability: toStateBoolean,
      layout: toStateLayout,
      time: toStateTime,
      period: toStatePeriod,
      term: toStateTerm,
    },
    renamed,
  );
};

export const getVisUrl = (state, dataflow = {}) =>
  `vis?${fromStateToSearch({ ...state, dataflow })}`;
